import { useState, useRef, useEffect, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import { training, trainingOps, chatWidget, documents as docsApi, products as productsApi, getAccount } from "../lib/supabase.js";

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const C = {
  ink: "#1a1a1a", dim: "#8a8a8a", faint: "#c0bdb8", line: "#e5e3de",
  bg: "#fafaf8", card: "#ffffff", accent: "#6366f1",
  good: "#2f9e6f", warn: "#b8862f", bad: "#c0503f",
};

const PROVIDER_MODELS = {
  openai: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1"],
  anthropic: ["claude-haiku-4-5-20251001", "claude-sonnet-4-6"],
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-2.0-flash"],
};

const TABS = [
  { id: "qa", label: "Q&A Pairs", blurb: "Trained answers that fire before the AI — exact or semantic match." },
  { id: "sources", label: "Knowledge", blurb: "Products, documents, and web pages the assistant retrieves from." },
  { id: "corrections", label: "Corrections", blurb: "Real answers that went wrong — fix them, then promote the best to Q&A." },
  { id: "persona", label: "Persona & Guardrails", blurb: "Voice, instructions, hard rules, and every AI knob." },
];

export default function Training() {
  const [tab, setTab] = useState("qa");
  const [qaPairs, setQaPairs] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadQa = useCallback(async () => {
    const { data } = await training.qaList();
    setQaPairs(data || []);
  }, []);

  const [prefill, setPrefill] = useState(null);
  useEffect(() => {
    (async () => {
      const [{ data: pairs }, { data: widgetCfg }] = await Promise.all([training.qaList(), chatWidget.get("default")]);
      setQaPairs(pairs || []);
      setCfg(widgetCfg || {});
      setLoading(false);
      // Deep link from Analytics: /training?train=<question> opens a
      // pre-filled Q&A editor so "coach your assistant" is one click.
      const trainQ = new URLSearchParams(window.location.search).get("train");
      if (trainQ) { setTab("qa"); setPrefill(trainQ); }
    })();
  }, []);

  return (
    <Layout>
      <div style={{ fontFamily: sans, color: C.ink, maxWidth: 1440 }}>
        <div style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 12 }}>
          <h1 style={{ margin: 0, fontFamily: serif, fontStyle: "italic", fontWeight: 400, fontSize: 34 }}>Training</h1>
          <span style={{ fontSize: 12, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>AI Assistant</span>
        </div>
        <p style={{ margin: "0 0 26px", fontSize: 14, color: C.dim, maxWidth: 560, lineHeight: 1.6 }}>
          Shape how the assistant answers. Everything here feeds the live widget on your site &mdash; preview it on the right as you go.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 420px", gap: 40, alignItems: "start" }}>
          <div>
            <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.line}`, marginBottom: 8, flexWrap: "wrap" }}>
              {TABS.map(t => {
                const on = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    background: "none", border: "none", borderBottom: on ? `2px solid ${C.ink}` : "2px solid transparent",
                    color: on ? C.ink : C.dim, padding: "10px 14px", fontSize: 12.5, fontWeight: on ? 700 : 500,
                    letterSpacing: "0.04em", textTransform: "uppercase", cursor: "pointer", marginBottom: -1, fontFamily: sans }}>
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p style={{ margin: "8px 0 22px", fontSize: 12.5, color: C.faint }}>{TABS.find(t => t.id === tab).blurb}</p>

            {loading ? <div style={{ fontSize: 13, color: C.dim, padding: 30 }}>Loading&hellip;</div> : (
              <>
                {tab === "qa" && <QAPairs items={qaPairs} reload={loadQa} prefill={prefill} onPrefillConsumed={() => setPrefill(null)} />}
                {tab === "sources" && <Sources />}
                {tab === "corrections" && <Corrections reloadQa={loadQa} />}
                {tab === "persona" && cfg && <Persona cfg={cfg} setCfg={setCfg} />}
              </>
            )}
          </div>

          <div style={{ position: "sticky", top: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: C.dim, marginBottom: 10 }}>Live preview</div>
            <WidgetPreview qaPairs={qaPairs.filter(p => p.is_active)} cfg={cfg || {}} />
            <p style={{ fontSize: 11.5, color: C.faint, marginTop: 12, lineHeight: 1.55 }}>
              This talks to the real engine — trained Q&A, knowledge retrieval, guardrails, all of it — but nothing here is logged or counted. The tag under each answer shows which path answered.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function WidgetPreview({ qaPairs, cfg }) {
  const account = getAccount();
  const greeting = cfg.greeting_message || "Hi! Ask me anything about our products.";
  const [msgs, setMsgs] = useState([{ role: "bot", text: greeting }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const scroller = useRef(null);
  useEffect(() => { scroller.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs, thinking]);
  useEffect(() => { setMsgs([{ role: "bot", text: greeting }]); }, [greeting]);

  // Real calls to the live engine — preview mode, so nothing is logged,
  // no session is created, and QA use-counts don't inflate.
  const send = async () => {
    const q = input.trim(); if (!q || thinking) return;
    setInput("");
    const history = msgs.filter(m => m.role === "user" || m.role === "bot").map(m => ({ role: m.role === "bot" ? "assistant" : "user", content: m.text }));
    setMsgs(m => [...m, { role: "user", text: q }]);
    setThinking(true);
    try {
      const res = await fetch(`https://amtdvoginmkwzbkwrdld.supabase.co/functions/v1/chat-ai?account=${account?.slug || "morryde"}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, history, configSlug: "default", preview: true }),
      });
      const d = await res.json();
      const via = d.meta?.searchMethod === "qa_pair" ? `Trained Q&A (${(d.meta.model || "").split(":")[1] || "match"})`
        : d.meta?.docChunks > 0 ? `AI · ${d.meta.docChunks} doc excerpt${d.meta.docChunks > 1 ? "s" : ""}${d.products?.length ? " + catalog" : ""}`
        : d.products?.length ? "AI · catalog retrieval" : "AI · no retrieval hit";
      setMsgs(m => [...m, { role: "bot", text: d.answer || d.error || "(no answer)", via, products: d.products || [] }]);
    } catch (err) {
      setMsgs(m => [...m, { role: "bot", text: "Preview couldn't reach the engine: " + err.message, via: "error" }]);
    }
    setThinking(false);
  };

  let suggestions = [];
  try { suggestions = Array.isArray(cfg.suggested_questions) ? cfg.suggested_questions.slice(0, 3) : []; } catch { suggestions = []; }
  if (!suggestions.length) suggestions = qaPairs.slice(0, 3).map(p => p.question);

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.06)" }}>
      <div style={{ background: "#0d0d14", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.accent, display: "grid", placeItems: "center", color: "#fff", fontSize: 14, fontWeight: 700 }}>{(cfg.avatar_text || "A").slice(0, 1)}</div>
          <div>
            <div style={{ color: "#fff", fontSize: 13.5, fontWeight: 600 }}>{cfg.header_title || "Assistant"}</div>
            <div style={{ color: "#7ee0a8", fontSize: 10.5, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#3ecf8e", display: "inline-block" }} /> Live engine
            </div>
          </div>
        </div>
        <span style={{ fontSize: 9, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: "0.1em" }}>not logged</span>
      </div>
      <div ref={scroller} style={{ height: 320, overflowY: "auto", padding: 14, background: "#f7f6f3", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "86%" }}>
            <div style={{
              background: m.role === "user" ? C.accent : "#fff", color: m.role === "user" ? "#fff" : C.ink,
              border: m.role === "user" ? "none" : `1px solid ${C.line}`, borderRadius: 12,
              padding: "9px 12px", fontSize: 12.5, lineHeight: 1.5,
              borderBottomRightRadius: m.role === "user" ? 3 : 12, borderBottomLeftRadius: m.role === "bot" ? 3 : 12,
            }}>{m.role === "bot" ? <Linkified text={m.text} /> : m.text}</div>
            {m.products?.length > 0 && (
              <div style={{ marginTop: 5, fontSize: 10.5, color: C.dim }}>{m.products.length} product card{m.products.length > 1 ? "s" : ""}: {m.products.map(p => p.name).join(", ")}</div>
            )}
            {m.via && (
              <div style={{ marginTop: 4, fontSize: 9.5, color: m.via === "error" ? C.bad : C.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{m.via}</div>
            )}
          </div>
        ))}
        {thinking && <div style={{ fontSize: 11, color: C.faint, padding: "2px 4px" }}>Thinking&hellip;</div>}
      </div>
      <div style={{ display: "flex", gap: 6, padding: "10px 12px 0", flexWrap: "wrap" }}>
        {cfg.lead_capture_config && (
          <button onClick={() => setMsgs(m => [...m, { role: "bot", text: "(In the live widget, this chip opens your lead capture form \u2014 name, email, and message go straight to the Leads page.)", via: "lead capture" }])} style={{
            background: C.accent + "15", border: `1px solid ${C.accent}55`, borderRadius: 20, padding: "4px 10px",
            fontSize: 11, color: C.accent, cursor: "pointer", fontWeight: 600, fontFamily: sans }}>
            {cfg.lead_capture_config?.chip_label || "Leave my contact info"}
          </button>
        )}
        {suggestions.map(sug => (
          <button key={sug} onClick={() => setInput(sug)} style={{
            background: "#f0eee9", border: `1px solid ${C.line}`, borderRadius: 20, padding: "4px 10px",
            fontSize: 11, color: C.dim, cursor: "pointer", fontFamily: sans }}>{sug}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, padding: 12 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
          placeholder={cfg.placeholder_text || "Ask the real engine\u2026"} style={{ flex: 1, border: `1px solid ${C.line}`, borderRadius: 9, padding: "9px 11px", fontSize: 12.5, outline: "none", fontFamily: sans, color: C.ink }} />
        <button onClick={send} disabled={thinking} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 9, padding: "0 15px", fontSize: 13, fontWeight: 600, cursor: thinking ? "default" : "pointer", fontFamily: sans, opacity: thinking ? 0.6 : 1 }}>Send</button>
      </div>
    </div>
  );
}

function Linkified({ text }) {
  // Renders markdown links, bare URLs, and phone numbers as real anchors —
  // matching what the production widget does with the same answer text.
  const parts = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<)]+)|(\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})/g;
  let last = 0, mt, key = 0;
  while ((mt = re.exec(text)) !== null) {
    if (mt.index > last) parts.push(text.slice(last, mt.index));
    const a = { color: C.accent, textDecoration: "underline", fontWeight: 600 };
    if (mt[1] && mt[2]) parts.push(<a key={key++} href={mt[2]} target="_blank" rel="noreferrer" style={a}>{mt[1]}</a>);
    else if (mt[3]) parts.push(<a key={key++} href={mt[3]} target="_blank" rel="noreferrer" style={a}>{mt[3].replace(/^https?:\/\//, "")}</a>);
    else if (mt[4]) parts.push(<a key={key++} href={"tel:" + mt[4].replace(/\D/g, "")} style={a}>{mt[4]}</a>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function QAPairs({ items, reload, prefill, onPrefillConsumed }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (prefill) {
      setEditing({ question: prefill, answer: "", match_mode: "semantic", match_threshold: 0.85, is_active: true, isNew: true });
      onPrefillConsumed?.();
    }
  }, [prefill]); // eslint-disable-line

  const toggle = async (it) => { await training.qaUpdate(it.id, { is_active: !it.is_active }); reload(); };
  const remove = async (id) => { if (!confirm("Delete this Q&A pair?")) return; await training.qaDelete(id); reload(); };

  const save = async () => {
    setBusy(true);
    const payload = {
      question: editing.question.trim(), answer: editing.answer.trim(),
      match_mode: editing.match_mode || "semantic",
      match_threshold: editing.match_threshold ?? 0.85,
      is_active: editing.is_active !== false,
    };
    let saved;
    if (editing.isNew) ({ data: saved } = await training.qaCreate(payload));
    else ({ data: saved } = await training.qaUpdate(editing.id, payload));
    setEditing(null);
    reload();
    // Embed in the background so semantic matching works immediately
    if (saved?.id) trainingOps("embed_qa", { id: saved.id }).then(reload);
    setBusy(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12.5, color: C.dim }}>{items.filter(i => i.is_active).length} active &middot; {items.length} total</span>
        <PrimaryBtn onClick={() => setEditing({ question: "", answer: "", match_mode: "semantic", match_threshold: 0.85, is_active: true, isNew: true })}>+ Add pair</PrimaryBtn>
      </div>
      {items.length === 0 && <Empty>No Q&A pairs yet. Add the questions you want answered word-for-word &mdash; warranty language, policies, top fitment questions.</Empty>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map(it => (
          <Card key={it.id} style={{ opacity: it.is_active ? 1 : 0.6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{it.question}</span>
                  {it.is_active ? <Tag c={C.good}>Active</Tag> : <Tag c={C.faint}>Paused</Tag>}
                  <Tag c={it.match_mode === "exact" ? C.warn : C.accent}>{it.match_mode === "exact" ? "Exact" : "Semantic"}</Tag>
                  {it.source === "correction" && <Tag c={C.dim}>From correction</Tag>}
                  {!it.question_embedding && it.match_mode !== "exact" && <Tag c={C.bad}>Not embedded</Tag>}
                </div>
                <p style={{ margin: 0, fontSize: 12.5, color: C.dim, lineHeight: 1.55 }}>{it.answer}</p>
                <div style={{ marginTop: 8, fontSize: 11, color: C.faint }}>Used in {it.use_count || 0} conversations{it.last_used_at ? ` \u00b7 last ${new Date(it.last_used_at).toLocaleDateString()}` : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <MiniBtn onClick={() => toggle(it)}>{it.is_active ? "Pause" : "Activate"}</MiniBtn>
                <MiniBtn onClick={() => setEditing({ ...it })}>Edit</MiniBtn>
                <MiniBtn onClick={() => remove(it.id)} danger>Delete</MiniBtn>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {editing && (
        <Editor title={editing.isNew ? "New Q&A pair" : "Edit Q&A pair"} onClose={() => setEditing(null)} onSave={save} busy={busy}>
          <Field label="Trigger question"><input value={editing.question} onChange={e => setEditing({ ...editing, question: e.target.value })} placeholder="What customers ask\u2026" style={inp} /></Field>
          <Field label="Exact answer the assistant gives"><textarea value={editing.answer} onChange={e => setEditing({ ...editing, answer: e.target.value })} rows={4} placeholder="Word-for-word response\u2026" style={{ ...inp, resize: "vertical" }} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Match mode">
              <select value={editing.match_mode} onChange={e => setEditing({ ...editing, match_mode: e.target.value })} style={inp}>
                <option value="semantic">Semantic (similar questions match)</option>
                <option value="exact">Exact (identical wording only)</option>
              </select>
            </Field>
            {editing.match_mode !== "exact" && (
              <Field label={`Match threshold \u00b7 ${Number(editing.match_threshold ?? 0.85).toFixed(2)}`}>
                <input type="range" min="0.70" max="0.98" step="0.01" value={editing.match_threshold ?? 0.85}
                  onChange={e => setEditing({ ...editing, match_threshold: parseFloat(e.target.value) })} style={{ width: "100%" }} />
              </Field>
            )}
          </div>
        </Editor>
      )}
    </div>
  );
}

function Sources() {
  const [rows, setRows] = useState({ products: 0, docs: [], urls: [] });
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [{ data: prods }, { data: docs }, { data: urls }] = await Promise.all([
      productsApi.list({ active: true }), docsApi.list(), training.sourcesList(),
    ]);
    const urlDocIds = new Set((urls || []).map(u => u.document_id).filter(Boolean));
    setRows({
      products: (prods || []).length,
      docs: (docs || []).filter(d => !urlDocIds.has(d.id)),
      urls: urls || [],
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const addUrl = async () => {
    let u = newUrl.trim(); if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    const { data: src } = await training.sourceCreate({ source_type: "url", url: u, sync_status: "pending" });
    setAdding(false); setNewUrl(""); load();
    if (src?.id) { trainingOps("sync_source", { source_id: src.id }).then(load); }
  };
  const resync = async (id) => { setBusyId(id); await trainingOps("sync_source", { source_id: id }); setBusyId(null); load(); };
  const removeUrl = async (id) => { if (!confirm("Remove this source and its indexed content?")) return; await training.sourceDelete(id); load(); };

  const statusMap = { synced: [C.good, "Synced"], syncing: [C.warn, "Syncing"], pending: [C.warn, "Pending"], error: [C.bad, "Needs attention"] };
  const rel = (d) => d ? new Date(d).toLocaleString() : "\u2014";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12.5, color: C.dim }}>The assistant retrieves from these when answering.</span>
        <PrimaryBtn onClick={() => setAdding(true)}>+ Add web page</PrimaryBtn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SourceCard kind="Products" name="Product Catalog" detail={`${rows.products} active products \u00b7 embedded for semantic search`} tag={[C.good, "Live"]} when="always current" />
        {rows.docs.map(d => (
          <SourceCard key={d.id} kind={(d.file_type || "DOC").toUpperCase().slice(0, 4)} name={d.title || d.original_filename}
            detail={`${d.page_count ? d.page_count + " pages \u00b7 " : ""}${d.chunk_count || 0} chunks indexed`}
            tag={d.status === "completed" ? [C.good, "Synced"] : d.status === "processing" ? [C.warn, "Processing"] : [C.bad, d.status]}
            when={rel(d.updated_at)} />
        ))}
        {rows.urls.map(u => {
          const [c, lbl] = statusMap[u.sync_status] || [C.dim, u.sync_status];
          return (
            <SourceCard key={u.id} kind="URL" name={u.url.replace(/^https?:\/\//, "")}
              detail={u.sync_status === "synced" ? `${u.crawl_page_count} page indexed` : u.sync_error || "Waiting to sync"}
              tag={[c, lbl]} when={rel(u.last_synced_at)}
              actions={<>
                <MiniBtn onClick={() => resync(u.id)}>{busyId === u.id ? "Syncing\u2026" : "Re-sync"}</MiniBtn>
                <MiniBtn onClick={() => removeUrl(u.id)} danger>Remove</MiniBtn>
              </>}
              error={u.sync_status === "error" ? (u.sync_error || "Sync failed") + " \u2014 check the URL is public, then re-sync." : null} />
          );
        })}
      </div>
      {adding && (
        <Editor title="Add a web page" onClose={() => setAdding(false)} onSave={addUrl}>
          <Field label="Page URL"><input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="morryde.com/support/faq" style={inp} autoFocus /></Field>
          <p style={{ fontSize: 11.5, color: C.faint, margin: 0, lineHeight: 1.5 }}>The page is fetched, cleaned, chunked, and embedded so the assistant can quote it. Re-sync any time the page changes.</p>
        </Editor>
      )}
    </div>
  );
}

function SourceCard({ kind, name, detail, tag, when, actions, error }) {
  const [c, lbl] = tag;
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 9, background: "#f0eee9", display: "grid", placeItems: "center", flexShrink: 0, fontSize: 9, fontWeight: 700, color: C.dim, letterSpacing: "0.06em" }}>{kind}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
          <div style={{ fontSize: 12, color: C.dim }}>{detail}</div>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>{actions}<Tag c={c}>{lbl}</Tag></div>
          <span style={{ fontSize: 10.5, color: C.faint }}>{when}</span>
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 11, paddingTop: 11, borderTop: `1px solid ${C.line}`, fontSize: 12, color: C.bad }}>{error}</div>
      )}
    </Card>
  );
}

function Corrections({ reloadQa }) {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await training.correctionsList();
    setConvos((data || []).filter(c => c.status !== "dismissed"));
  }, []);

  useEffect(() => {
    (async () => {
      setScanning(true);
      await training.buildQueue();   // pull in fresh thumbs-downs + content gaps
      await load();
      setScanning(false);
    })();
  }, [load]);

  const saveCorrection = async (c) => {
    await training.correctionUpdate(c.id, { corrected_answer: draft, status: "corrected" });
    setActive(null); load();
  };
  const approve = async (c) => { await training.correctionUpdate(c.id, { status: "approved_as_is" }); load(); };
  const dismiss = async (c) => { await training.correctionUpdate(c.id, { status: "dismissed" }); load(); };
  const promote = async (c) => {
    setBusyId(c.id);
    const res = await trainingOps("promote_correction", { correction_id: c.id });
    setBusyId(null);
    if (res?.error) alert(res.error); else { load(); reloadQa(); }
  };

  const flagLabel = (c) => {
    if (c.flag_reason?.startsWith("content_gap:")) return [C.warn, "Repeated unanswered question"];
    if (c.flag_reason?.includes("thumbs-down")) return [C.bad, "Visitor gave a thumbs-down"];
    if (c.status === "corrected") return [C.good, c.qa_pair_id ? "Corrected \u00b7 promoted to Q&A" : "Corrected \u00b7 teaching the assistant"];
    if (c.status === "approved_as_is") return [C.good, "Approved as-is"];
    return [C.dim, c.flag_reason || "Flagged for review"];
  };

  return (
    <div>
      <span style={{ fontSize: 12.5, color: C.dim }}>
        {scanning ? "Scanning recent conversations for problems\u2026" : "Review real conversations. Correct any answer and the assistant learns the better response."}
      </span>
      {!scanning && convos.length === 0 && <Empty style={{ marginTop: 14 }}>Nothing to review. Thumbs-downs and repeated unanswered questions land here automatically.</Empty>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
        {convos.map(c => {
          const [fc, fl] = flagLabel(c);
          return (
            <Card key={c.id}>
              <div style={{ fontSize: 11, color: C.faint, marginBottom: 5 }}>Visitor asked</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 11 }}>{c.visitor_question}</div>
              <div style={{ background: C.bg, border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 11px" }}>
                <div style={{ fontSize: 10.5, color: C.faint, marginBottom: 3 }}>Assistant replied</div>
                <div style={{ fontSize: 12.5, color: C.dim, lineHeight: 1.5 }}>{c.original_answer}</div>
              </div>
              {c.corrected_answer && (
                <div style={{ marginTop: 10, background: "#f0f7f3", border: `1px solid ${C.good}55`, borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 10.5, color: C.good, marginBottom: 3, fontWeight: 700 }}>Corrected &mdash; now taught</div>
                  <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>{c.corrected_answer}</div>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 11, gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: fc }}>{fl}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {c.status === "pending" && active !== c.id && <>
                    <MiniBtn onClick={() => approve(c)}>Answer was fine</MiniBtn>
                    <MiniBtn onClick={() => dismiss(c)}>Dismiss</MiniBtn>
                    <MiniBtn onClick={() => { setActive(c.id); setDraft(c.original_answer.startsWith("(") ? "" : c.original_answer); }}>Correct this</MiniBtn>
                  </>}
                  {c.status === "corrected" && !c.qa_pair_id && (
                    <MiniBtn onClick={() => promote(c)}>{busyId === c.id ? "Promoting\u2026" : "Promote to Q&A pair"}</MiniBtn>
                  )}
                </div>
              </div>
              {active === c.id && (
                <div style={{ marginTop: 11 }}>
                  <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} placeholder="The answer the assistant should have given\u2026" style={{ ...inp, resize: "vertical" }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    <MiniBtn onClick={() => setActive(null)}>Cancel</MiniBtn>
                    <PrimaryBtn onClick={() => saveCorrection(c)}>Save correction</PrimaryBtn>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Persona({ cfg, setCfg }) {
  const [tone, setTone] = useState(cfg.ai_tone || "friendly");
  const [instructions, setInstructions] = useState(cfg.ai_system_prompt || "");
  const [knobs, setKnobs] = useState({
    ai_provider: cfg.ai_provider || "openai",
    ai_model: cfg.ai_model || "gpt-4o-mini",
    ai_temperature: cfg.ai_temperature ?? 0.3,
    ai_max_tokens: cfg.ai_max_tokens || 1024,
    ai_retrieval_threshold: cfg.ai_retrieval_threshold ?? 0.35,
    ai_qa_enabled: cfg.ai_qa_enabled !== false,
    ai_corrections_enabled: cfg.ai_corrections_enabled !== false,
  });
  const [guardrails, setGuardrails] = useState([]);
  const [newRail, setNewRail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const loadRails = useCallback(async () => {
    const { data } = await training.guardrailsList();
    setGuardrails(data || []);
  }, []);
  useEffect(() => { loadRails(); }, [loadRails]);

  const toggleRail = async (g) => { await training.guardrailUpdate(g.id, { is_active: !g.is_active }); loadRails(); };
  const deleteRail = async (id) => { await training.guardrailDelete(id); loadRails(); };
  const addRail = async () => {
    if (!newRail?.rule_text?.trim()) return;
    await training.guardrailCreate({ rule_text: newRail.rule_text.trim(), rule_type: newRail.rule_type || "restriction", sort_order: guardrails.length });
    setNewRail(null); loadRails();
  };

  const savePersona = async () => {
    setSaving(true);
    const payload = { ai_tone: tone, ai_system_prompt: instructions, ...knobs };
    const { data } = await chatWidget.upsertBySlug("default", { name: cfg.name || "Default", ...payload });
    if (data) setCfg({ ...cfg, ...data });
    setSaving(false); setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 2500);
  };

  const tones = ["friendly", "professional", "concise", "enthusiastic"];
  const isCustom = !tones.includes(tone);
  const railTypes = [["restriction", "Never do"], ["requirement", "Always do"], ["redirect", "Redirect"]];
  const K = (label, children) => (
    <div><div style={{ fontSize: 11, color: C.dim, marginBottom: 5 }}>{label}</div>{children}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 620 }}>
      <Card>
        <Lbl>Voice</Lbl>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {tones.map(t => (
            <button key={t} onClick={() => setTone(t)} style={{
              flex: 1, padding: "9px", borderRadius: 8, textTransform: "capitalize", fontSize: 12.5, cursor: "pointer", fontFamily: sans,
              background: tone === t ? "#eeeefc" : C.bg, color: tone === t ? C.accent : C.dim,
              border: `1px solid ${tone === t ? C.accent : C.line}`, fontWeight: tone === t ? 700 : 500 }}>{t}</button>
          ))}
          <button onClick={() => setTone(isCustom ? tone : "Speak like ")} style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 12.5, cursor: "pointer", fontFamily: sans,
            background: isCustom ? "#eeeefc" : C.bg, color: isCustom ? C.accent : C.dim,
            border: `1px solid ${isCustom ? C.accent : C.line}`, fontWeight: isCustom ? 700 : 500 }}>Custom</button>
        </div>
        {isCustom && (
          <div style={{ marginTop: 10 }}>
            <input value={tone} onChange={e => setTone(e.target.value)} placeholder="Describe the voice in one line \u2014 e.g. Talk like a veteran RV tech: plainspoken, safety-first, no fluff"
              style={{ ...inp }} />
            <div style={{ fontSize: 11, color: C.faint, marginTop: 5 }}>This line goes into the prompt verbatim as the voice instruction.</div>
          </div>
        )}
      </Card>
      <Card>
        <Lbl>Instructions</Lbl>
        <div style={{ fontSize: 11.5, color: C.faint, margin: "3px 0 10px" }}>The assistant's core briefing &mdash; its job and boundaries.</div>
        <textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={5} placeholder="You are the product assistant for\u2026" style={{ ...inp, resize: "vertical" }} />
      </Card>
      <Card>
        <Lbl>Guardrails</Lbl>
        <div style={{ fontSize: 11.5, color: C.faint, margin: "3px 0 12px" }}>Hard rules composed into every response &mdash; grouped by type in the prompt.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {guardrails.map(g => (
            <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 11px", background: C.bg, border: `1px solid ${C.line}`, borderRadius: 8 }}>
              <button onClick={() => toggleRail(g)}
                style={{ width: 34, height: 20, borderRadius: 20, border: "none", background: g.is_active ? C.accent : C.faint, position: "relative", flexShrink: 0, cursor: "pointer" }}>
                <span style={{ position: "absolute", top: 2, left: g.is_active ? 16 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
              </button>
              <Tag c={g.rule_type === "restriction" ? C.bad : g.rule_type === "requirement" ? C.good : C.warn}>
                {railTypes.find(([t]) => t === g.rule_type)?.[1] || g.rule_type}
              </Tag>
              <span style={{ fontSize: 12.5, color: g.is_active ? C.ink : C.faint, flex: 1 }}>{g.rule_text}</span>
              <MiniBtn onClick={() => deleteRail(g.id)} danger>&times;</MiniBtn>
            </div>
          ))}
          {!newRail && <MiniBtn onClick={() => setNewRail({ rule_text: "", rule_type: "restriction" })}>+ Add guardrail</MiniBtn>}
          {newRail && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select value={newRail.rule_type} onChange={e => setNewRail({ ...newRail, rule_type: e.target.value })} style={{ ...inp, width: 130 }}>
                {railTypes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={newRail.rule_text} onChange={e => setNewRail({ ...newRail, rule_text: e.target.value })} onKeyDown={e => e.key === "Enter" && addRail()}
                placeholder="Never quote shipping prices \u2014 link to the calculator" style={{ ...inp, flex: 1 }} autoFocus />
              <PrimaryBtn onClick={addRail}>Add</PrimaryBtn>
            </div>
          )}
        </div>
      </Card>
      <Card>
        <Lbl>Engine</Lbl>
        <div style={{ fontSize: 11.5, color: C.faint, margin: "3px 0 12px" }}>Every knob the live assistant runs on.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {K("Provider",
            <select value={knobs.ai_provider} onChange={e => { const p = e.target.value; setKnobs({ ...knobs, ai_provider: p, ai_model: (PROVIDER_MODELS[p] || [])[0] || "" }); }} style={inp}>
              <option value="openai">OpenAI</option><option value="anthropic">Anthropic</option><option value="gemini">Gemini</option>
            </select>)}
          {K("Model",
            <select value={knobs.ai_model} onChange={e => setKnobs({ ...knobs, ai_model: e.target.value })} style={inp}>
              {(PROVIDER_MODELS[knobs.ai_provider] || []).concat(PROVIDER_MODELS[knobs.ai_provider]?.includes(knobs.ai_model) ? [] : [knobs.ai_model]).filter(Boolean).map(m => <option key={m} value={m}>{m}</option>)}
            </select>)}
          {K(`Temperature \u00b7 ${Number(knobs.ai_temperature).toFixed(2)}`,
            <input type="range" min="0" max="1" step="0.05" value={knobs.ai_temperature} onChange={e => setKnobs({ ...knobs, ai_temperature: parseFloat(e.target.value) })} style={{ width: "100%" }} />)}
          {K("Max tokens", <input type="number" value={knobs.ai_max_tokens} onChange={e => setKnobs({ ...knobs, ai_max_tokens: parseInt(e.target.value) || 1024 })} style={inp} />)}
          {K(`Retrieval threshold \u00b7 ${Number(knobs.ai_retrieval_threshold).toFixed(2)}`,
            <input type="range" min="0.2" max="0.7" step="0.05" value={knobs.ai_retrieval_threshold} onChange={e => setKnobs({ ...knobs, ai_retrieval_threshold: parseFloat(e.target.value) })} style={{ width: "100%" }} />)}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
              <input type="checkbox" checked={knobs.ai_qa_enabled} onChange={e => setKnobs({ ...knobs, ai_qa_enabled: e.target.checked })} /> Q&A pairs answer first
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
              <input type="checkbox" checked={knobs.ai_corrections_enabled} onChange={e => setKnobs({ ...knobs, ai_corrections_enabled: e.target.checked })} /> Corrections teach by example
            </label>
          </div>
        </div>
      </Card>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
        {savedAt && <span style={{ fontSize: 12, color: C.good }}>Saved &mdash; live now</span>}
        <PrimaryBtn onClick={savePersona}>{saving ? "Saving\u2026" : "Save persona"}</PrimaryBtn>
      </div>
    </div>
  );
}

function Card({ children, style }) { return <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 15, ...style }}>{children}</div>; }
function Tag({ children, c }) { return <span style={{ fontSize: 10, fontWeight: 700, color: c, background: `${c}18`, border: `1px solid ${c}44`, borderRadius: 20, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{children}</span>; }
function Lbl({ children }) { return <div style={{ fontSize: 13, fontWeight: 700 }}>{children}</div>; }
function Empty({ children, style }) { return <div style={{ padding: "26px 20px", textAlign: "center", fontSize: 12.5, color: C.faint, background: C.card, border: `1px dashed ${C.line}`, borderRadius: 12, lineHeight: 1.6, ...style }}>{children}</div>; }
function PrimaryBtn({ children, onClick }) { return <button onClick={onClick} style={{ background: C.ink, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: sans, letterSpacing: "0.03em" }}>{children}</button>; }
function MiniBtn({ children, onClick, danger }) { return <button onClick={onClick} style={{ background: "none", border: `1px solid ${C.line}`, color: danger ? C.bad : C.dim, borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 500, cursor: "pointer", fontFamily: sans, whiteSpace: "nowrap" }}>{children}</button>; }
const inp = { width: "100%", background: C.bg, border: `1px solid ${C.line}`, borderRadius: 8, padding: "9px 11px", color: C.ink, fontSize: 12.5, outline: "none", fontFamily: sans, boxSizing: "border-box" };
function Field({ label, children }) { return <div style={{ marginBottom: 13 }}><div style={{ fontSize: 11.5, color: C.dim, marginBottom: 6 }}>{label}</div>{children}</div>; }
function Editor({ title, children, onSave, onClose, busy }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "grid", placeItems: "center", padding: 20, zIndex: 50 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 22, width: "100%", maxWidth: 520, fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{title}</div>
        {children}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <MiniBtn onClick={onClose}>Cancel</MiniBtn><PrimaryBtn onClick={onSave}>{busy ? "Saving\u2026" : "Save"}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
